import type {
  EditContributionRefundRequest,
  EditContributionRefundResponse,
  LinkRefundToParentRequest,
  OrphanStandaloneRefundRequest,
  RefundMutationResponse,
  UncheckRefundRequest,
  RowVersion,
  TriageItem,
} from '@signalvote/shared';
import { defineWrite } from '@/lib/data';
import { apiJson, apiResponse } from './apiFetch';
import { refusal, throwStagedRowError } from './useFinanceContributions';
import {
  editInboxRow,
  markRefundLinked,
  markRefundStandalone,
  markRefundOrphan,
} from './sortInboxOptimistic';

// USI-011 (ADR-011 § Structural commitment #4; design doc § Key decisions #3):
// orphan-refund pick-parent / skip-to-standalone mutations. The triage
// modal's OrphanRefundBody is the first frontend consumer.
//
// The backend service `IContributionRefundLinkService` enforces:
//   - Refund tenant filter → 404 RefundNotFound.
//   - Refund.ContributionId != null → 409 AlreadyLinked.
//   - Stale concurrency token → 409 StaleWrite.
//   - Original contribution tenant filter (link only) → 404
//     OriginalContributionNotFound.
//
// Every write here is a row write: it sends the refund's freshest token when it runs and queues
// behind any other write to the same refund. A success returns the refund's new token plus the
// ADR-019 federal-gated `advisory` (null outside a calendar-locked FEDERAL period), so a caller
// can surface the nudge. A refusal is the server's failure: `AlreadyLinked` reads as refused with
// its code, a lost race as stale.

type RefundAction = 'link' | 'orphan-standalone' | 'unlink' | 'revoke-standalone';

/**
 * POST one link-service action. Each answers 200 with the refund's new token and the ADR-019
 * federal-gated advisory (null outside a calendar-locked federal period).
 */
function postRefundAction(id: string, action: RefundAction, json: object, fallback: string) {
  return apiJson<RefundMutationResponse>(`/api/finance/contribution-refunds/${id}/${action}`, {
    method: 'POST',
    json,
    error: refusal(fallback),
  });
}

const refundRow = (id: string) => (item: TriageItem) => item.kind === 'Refund' && item.refund.id === id;

/**
 * Resolve or reopen an orphan refund: patch its sort-inbox row on click. The write door rolls it
 * back on a failure, and the write set refreshes the inbox so the row leaves (or rejoins) the queue.
 */
function refundAction<B>(
  action: RefundAction,
  toBody: (concurrencyVersion: RowVersion, body: B) => object,
  mark: (item: TriageItem) => TriageItem,
  fallback: string,
) {
  return defineWrite({
    row: 'ContributionRefund',
    send: (id, concurrencyVersion: RowVersion, body: B) => postRefundAction(id, action, toBody(concurrencyVersion, body), fallback),
    retry: 'transient',
    patch: ({ row }) => [editInboxRow(refundRow(row.id), mark)],
  });
}

/**
 * POST /api/finance/contribution-refunds/{id}/link — link an orphan refund to a chosen parent
 * contribution. The backend writes one `RefundLinkedToParent` audit row. The row resolves to
 * Done/Linked on click.
 */
export const useLinkRefundToParent = refundAction<{
  originalContributionId: string;
  /**
   * USI-011: candidate set the FE displayed at choice time. Rides into the
   * `RefundLinkedToParent` audit row so the §17-5-8 trail records the candidates the treasurer
   * rejected before picking, per ADR-011 § Negative consequences.
   */
  suggestedOriginalIds?: string[];
}>(
  'link',
  (concurrencyVersion, { originalContributionId, suggestedOriginalIds }) =>
    ({
      originalContributionId,
      concurrencyVersion,
      suggestedOriginalIds: suggestedOriginalIds ?? [],
    }) satisfies LinkRefundToParentRequest,
  markRefundLinked,
  'Failed to link refund to parent',
);

/**
 * POST /api/finance/contribution-refunds/{id}/orphan-standalone — record the treasurer's
 * deliberate choice to leave the refund as a standalone negative-amount row (Shape A:
 * ContributionId stays null). The backend writes one `RefundOrphanStandaloneChosen` audit row
 * carrying the optional reason, the §17-5-8 evidence the audit trail relies on post-ADR-011, and
 * that row drops the refund from the sort inbox for good.
 */
export const useOrphanRefundStandalone = refundAction<{
  reason?: string;
  /** USI-011: the parents the treasurer turned down before choosing standalone; `[]` is fine. */
  suggestedOriginalIds?: string[];
}>(
  'orphan-standalone',
  (concurrencyVersion, { reason, suggestedOriginalIds }) =>
    ({
      concurrencyVersion,
      reason: reason && reason.trim() !== '' ? reason.trim() : null,
      suggestedOriginalIds: suggestedOriginalIds ?? [],
    }) satisfies OrphanStandaloneRefundRequest,
  markRefundStandalone,
  'Failed to record orphan standalone choice',
);

/**
 * POST /contribution-refunds/{id}/unlink, the inverse of `useLinkRefundToParent`: clicking a
 * Done/Linked refund's ✓ on the ledger severs the parent link, back to the orphan state.
 */
export const useUnlinkRefund = refundAction<object>(
  'unlink',
  (concurrencyVersion) => ({ concurrencyVersion }) satisfies UncheckRefundRequest,
  markRefundOrphan,
  'Failed to uncheck refund',
);

/**
 * POST /contribution-refunds/{id}/revoke-standalone, the inverse of `useOrphanRefundStandalone`.
 * The backend appends a `RefundOrphanStandaloneRevoked` audit row (the Chosen row is never
 * deleted; the sort-inbox Done signal is latest-event-wins), back to the orphan state.
 */
export const useRevokeRefundStandalone = refundAction<object>(
  'revoke-standalone',
  (concurrencyVersion) => ({ concurrencyVersion }) satisfies UncheckRefundRequest,
  markRefundOrphan,
  'Failed to uncheck refund',
);

/**
 * The triage ledger's inline edit: PATCH Amount, Reason or Date on a not-yet-filed refund. A
 * field left out is sent as null, which the server leaves unchanged.
 */
export const useEditContributionRefund = defineWrite({
  row: 'ContributionRefund',
  send: (id, concurrencyVersion: RowVersion, edit: Partial<Omit<EditContributionRefundRequest, 'concurrencyVersion'>>) =>
    apiJson<EditContributionRefundResponse>(`/api/finance/contribution-refunds/${id}`, {
      method: 'PATCH',
      json: {
        amount: edit.amount ?? null,
        reason: edit.reason ?? null,
        date: edit.date ?? null,
        concurrencyVersion,
      } satisfies EditContributionRefundRequest,
      error: refusal('Failed to edit refund'),
    }),
});

/**
 * ADR-016 single-row Delete — DELETE /api/finance/contribution-refunds/{id}.
 * Refunds are Delete-only in v1 (no Void / un-void): a wrong refund "never
 * happened". Distinct from the ✓-uncheck unlink/revoke flow above — Delete
 * removes the refund row outright. 204 No Content; the concurrency token rides
 * as a query param (DELETE carries no body). Deleting a refund can drop a
 * Confirmed refund-expenditure, which shifts the earliest staged date.
 */
export const useDeleteContributionRefund = defineWrite({
  row: 'ContributionRefund',
  send: async (id, concurrencyVersion: RowVersion): Promise<void> => {
    const response = await apiResponse(
      `/api/finance/contribution-refunds/${id}?concurrencyVersion=${concurrencyVersion}`,
      { method: 'DELETE' },
    );
    if (!response.ok) {
      // Delete failures carry the DeleteErrorCodes family, NOT the link/standalone family,
      // so surface the same StagedRowUpdateError the contribution and expenditure deletes throw.
      await throwStagedRowError(response, 'Failed to delete refund');
    }
  },
});
